import React from 'react';
import type { ReactElement } from 'react';
import { type MentionItem } from './shared-mention';

interface MentionListProps {
  items: MentionItem[];
  command: (props: { id: string }) => void;
}

interface MentionListRef {
  onKeyDown?: (props: { event: KeyboardEvent }) => boolean;
}

export const MentionList = React.forwardRef<MentionListRef, MentionListProps>(
  (props, ref) => {
    const [selectedIndex, setSelectedIndex] = React.useState(0);

    // Get only selectable items (not headers)
    const selectableItems = props.items;

    const selectItem = (index: number) => {
      const item = selectableItems[index];

      if (item) {
        props.command({ id: item.id });
      }
    };

    const upHandler = () => {
      setSelectedIndex(
        (selectedIndex + selectableItems.length - 1) % selectableItems.length,
      );
    };

    const downHandler = () => {
      setSelectedIndex((selectedIndex + 1) % selectableItems.length);
    };

    const enterHandler = () => {
      selectItem(selectedIndex);
    };

    React.useEffect(() => setSelectedIndex(0), [props.items]);

    React.useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          upHandler();
          return true;
        }

        if (event.key === 'ArrowDown') {
          downHandler();
          return true;
        }

        if (event.key === 'Enter') {
          enterHandler();
          return true;
        }

        return false;
      },
    }));

    // Group items by type with section headers
    const renderItems = () => {
      if (!props.items.length) {
        return <div className="item">No result</div>;
      }

      const appliances = props.items.filter(
        (item) => item.type === 'appliance',
      );
      const ingredients = props.items.filter(
        (item) => item.type === 'ingredient',
      );

      const elements: ReactElement[] = [];
      let currentIndex = 0;

      // Add appliances section
      if (appliances.length > 0) {
        elements.push(
          <div key="appliances-header" className="mention-section-header">
            Appliances
          </div>,
        );

        appliances.forEach((item) => {
          const isSelected = currentIndex === selectedIndex;
          elements.push(
            <button
              className={`mention-item appliance-item ${isSelected ? 'is-selected' : ''}`}
              key={`appliance-${item.id}`}
              onClick={() => selectItem(currentIndex)}
              type="button"
            >
              @{item.label}
            </button>,
          );
          currentIndex++;
        });
      }

      // Add ingredients section
      if (ingredients.length > 0) {
        elements.push(
          <div key="ingredients-header" className="mention-section-header">
            Ingredients
          </div>,
        );

        ingredients.forEach((item) => {
          const isSelected = currentIndex === selectedIndex;
          elements.push(
            <button
              className={`mention-item ingredient-item ${isSelected ? 'is-selected' : ''}`}
              key={`ingredient-${item.id}`}
              onClick={() => selectItem(currentIndex)}
              type="button"
            >
              @{item.label}
            </button>,
          );
          currentIndex++;
        });
      }

      return elements;
    };

    return <div className="dropdown-menu">{renderItems()}</div>;
  },
);

MentionList.displayName = 'MentionList';
